import { build_envelope, is_valid_envelope } from './envelope';
import type { BuildEnvelopeArgs } from './envelope';

/**
 * Sends a typed envelope to the opener window.
 *
 * CRITICAL: the second arg to opener.postMessage is the targetOrigin —
 * NOT `'*'`. Specifying the explicit return_origin means the browser
 * won't deliver the message if the opener has navigated to a different
 * origin since launch, which prevents leaking the sso_ticket to a page
 * that hijacked the opener via window.location after popup launch.
 *
 * Bails silently if the opener is null or already closed — there's no
 * useful action to take and throwing here would mask the original error
 * that prompted the send.
 *
 * @param opener        the window.opener handle stored on popup load
 * @param return_origin the origin we trust to receive the message
 * @param args          the envelope payload (incl. request_id)
 */
export function send_to_opener(
  opener: Window | null,
  return_origin: string,
  args: BuildEnvelopeArgs
): void {
  if (opener === null || opener.closed === true) return;
  opener.postMessage(build_envelope(args), return_origin);
}

/**
 * Registers a one-shot listener for an `abort` envelope coming back from
 * the opener and invokes `on_abort` when it arrives. The opener uses this
 * to tell the popup that the user cancelled the ceremony in the parent
 * window (closed the modal, navigated away, etc.).
 *
 * Returns a teardown that removes the listener — callers should hold onto
 * it and run it when the ceremony resolves or unmounts.
 *
 * @param return_origin       trusted opener origin (rejects messages from anywhere else)
 * @param expected_request_id the request_id the opener launched the popup with
 * @param on_abort            invoked once when a matching abort envelope arrives
 */
export function listen_for_abort(
  return_origin: string,
  expected_request_id: string,
  on_abort: () => void
): () => void {
  const handler = (event: MessageEvent) => {
    if (event.origin !== return_origin) return;
    if (is_valid_envelope(event.data, expected_request_id) === false) return;
    if (event.data.type === 'abort') {
      on_abort();
    }
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
